/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import moment from 'moment-timezone';
import { utcDateOrUndefined, validDateOrUndefined } from 'volta/utils/date-utils';

import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { BvCalendarSignature } from './../bv-calendar/index';
import type { BvTextFieldSignature } from './../bv-text-field/index';
import type { BvInlineEditApi } from '../bv-inline-edit';
import type { BvFocusUnitModifier } from '../bv-focus-zone';
import type { PowerCalendarDay } from 'ember-power-calendar/utils';
import type { BvPopoverSignature } from 'volta/components/bv-popover';
import type { PowerCalendarAPI } from 'ember-power-calendar/components/power-calendar';
import type { TDate } from 'volta/models/types/calendar';

type PopoverArgs = Pick<
  BvPopoverSignature['Args'],
  | 'horizontalPosition'
  | 'matchTriggerWidth'
  | 'onClose'
  | 'onOpen'
  | 'preventScroll'
  | 'renderInPlace'
>;

type BvDateFieldArgs = Omit<BvTextFieldSignature['Args'], 'value'> &
  PopoverArgs & {
    value?: TDate;
    minDate?: TDate;
    maxDate?: TDate;
    closeOnSelect?: boolean;
    withoutCalendar?: boolean;
    dateFormat?: string; // !BETTERTYPE!
    timeZone?: string; // !BETTERTYPE!
    testName?: string;
    dataName?: string;
    fullWidth?: boolean;
    allowClear?: boolean;
    focusZoneUnit?: BvFocusUnitModifier;
    size?: BvCalendarSignature['Args']['size'];
    iconClass?: string;
    enableFocusTrap?: boolean;
    onChange?: (value?: TDate) => void;
    onBlur?: (e: FocusEvent) => void;
    onFocus?: (e: FocusEvent) => void;
    onEnter?: (value?: TDate) => void;
    onClearButtonClick?: () => void;
  };

export interface BvDateFieldSignature {
  Args: BvDateFieldArgs;
  Blocks: {
    days: [
      {
        popover: BvInlineEditApi['popover'];
        calendar: PowerCalendarAPI;
        minDate?: TDate;
        maxDate?: TDate;
      }
    ];
    menu: [{ popover: BvInlineEditApi['popover']; calendar: PowerCalendarAPI }];
    trigger: [{ popover: BvInlineEditApi['popover'] }];
  };
  Element: HTMLElement;
}

export default class BvDateFieldComponent extends Component<BvDateFieldSignature> {
  @tracked calCenter = this.args.value
    ? moment.utc(this.args.value).toDate()
    : moment.utc().toDate();
  @tracked inputError: boolean = false;

  get validatedDate() {
    return this.validateInputDate(this.args.value)?.toDate();
  }

  get disabled() {
    return this.args.disabled === true || this.args.readOnly === true;
  }

  @action
  onChange(
    day: PowerCalendarDay,
    _calendar: PowerCalendarAPI,
    e: InputEvent,
    afterSelect?: (e: InputEvent) => void
  ) {
    e.stopPropagation();
    const { closeOnSelect = true } = this.args;
    this.inputError = false;

    this.args.onChange?.(day.date);

    if (closeOnSelect && typeof afterSelect === 'function') {
      afterSelect(e);
    }
  }

  @action
  onInputChange(value: string | number) {
    const mDate = this.validateInputDate(value);
    if (mDate) {
      this.inputError = false;
      this.calCenter = mDate.toDate();

      if (this.args.withoutCalendar) {
        this.args.onChange?.(mDate.toDate());
      }
    } else {
      this.inputError = true;
    }
  }

  @action
  handleInlineEditSave(event: Event) {
    if (!event?.target) {
      return;
    }
    const { value } = event.target as HTMLInputElement;
    this.handleTextFieldEnter(value);
  }

  @action
  handleTextFieldEnter(value: moment.MomentInput) {
    const { onEnter, onChange } = this.args;
    const validDate = this.validateInputDate(value);

    if (validDate) {
      this.inputError = false;
      if (typeof onEnter === 'function') {
        onEnter?.(validDate.toDate());
      } else {
        onChange?.(validDate.toDate());
      }
    }
  }

  @action
  handleCenterChange(center: PowerCalendarDay) {
    if (center.date) {
      this.calCenter = moment.utc(center.date).toDate();
    }
  }

  @action
  handleParentValueChange() {
    this.calCenter = moment.utc(this.args.value).toDate();
  }

  @action
  handlePopoverOpen() {
    this.calCenter = moment.utc(this.args.value).toDate();
    return this.args.onOpen?.();
  }

  @action
  handleClear(_id: string, _e: InputEvent) {
    this.calCenter = moment.utc().toDate();
    this.inputError = false;
    this.args.onChange?.(null);
  }

  /**
   * This function validate the date value input.
   * It checks the min and max date constraints as weel as general parsing validity.
   *
   * @param date Input date
   * @returns The parse Date or undefined if invalid or undefined
   */
  validateInputDate(date: moment.MomentInput) {
    const { minDate, maxDate } = this.args;
    const vMinDate = validDateOrUndefined(minDate);
    const vMaxDate = validDateOrUndefined(maxDate);
    const vDate = validDateOrUndefined(date);

    return vDate &&
      ((vMinDate && moment.utc(vDate).isBefore(vMinDate)) ||
        (vMaxDate && moment.utc(vDate).isAfter(vMaxDate)))
      ? undefined
      : vDate;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvDateField: typeof BvDateFieldComponent;
    'bv-date-field': typeof BvDateFieldComponent;
  }
}
